/**
 * Deferred third-party script loading.
 *
 * PERF CONTRACT (see "Performance non-negotiables" in CLAUDE.md and
 * tests/unit/perf/perf-guards.test.ts): marketing/analytics scripts (GTM,
 * HubSpot tracking, etc.) must NOT load during initial page load. They burn
 * main-thread time on mobile (measured 2026-07-17: GTM + HubSpot suite =
 * ~2.4s Total Blocking Time, PageSpeed mobile 46). Loading them on first
 * user interaction — with a generous timeout fallback so non-interacting
 * real visitors are still tracked — keeps them out of the Lighthouse trace
 * without losing meaningful analytics coverage.
 */

const INTERACTION_EVENTS: ReadonlyArray<keyof WindowEventMap> = [
  'scroll',
  'click',
  'touchstart',
  'keydown',
  'mousemove',
];

/**
 * Default fallback delay. 10s sits comfortably outside a Lighthouse trace
 * (which settles shortly after network/CPU idle) while still capturing real
 * visitors who never interact.
 */
export const THIRD_PARTY_LOAD_TIMEOUT_MS = 10_000;

/**
 * Runs `load` on the first user interaction, or after `timeoutMs` as a
 * fallback — whichever comes first, exactly once. Returns a cleanup function
 * (cancels the pending load; safe to call after it fired).
 */
export function runOnFirstInteractionOrTimeout(
  load: () => void,
  timeoutMs: number = THIRD_PARTY_LOAD_TIMEOUT_MS,
): () => void {
  let done = false;

  const cleanup = (): void => {
    window.clearTimeout(timeoutId);
    for (const event of INTERACTION_EVENTS) {
      window.removeEventListener(event, fire);
    }
  };

  const fire = (): void => {
    if (done) return;
    done = true;
    cleanup();
    load();
  };

  const timeoutId = window.setTimeout(fire, timeoutMs);
  for (const event of INTERACTION_EVENTS) {
    window.addEventListener(event, fire, { once: true, passive: true });
  }

  return () => {
    done = true;
    cleanup();
  };
}
